import type { AIStore } from '..';
import type { AIConfigsRepresentationType, UpdateAIConfigsRepresentationType } from '@readme/api/src/mappings/ai/types';
import type { StateCreator } from 'zustand';

import produce from 'immer';

import type useReadmeApi from '@core/hooks/useReadmeApi';
import { fetcher } from '@core/hooks/useReadmeApi';
import { actionLog } from '@core/store/util';

export type LinterSettingsNames = 'errors' | 'styleguide' | 'warnings';

export type AiLinterSliceState = AIConfigsRepresentationType['data'] & {
  /**
   * Path to the API /ai/configs url including the correct baseUrl.
   */
  apiUrl?: string;
  /**
   * Whether the store has initialized with its initial data.
   */
  isReady: boolean;
  /**
   * Wether any of the linting configs are currently saving.
   */
  isSaving: {
    errors?: boolean;
    warnings?: boolean;
    styleguide?: boolean;
  };
  /**
   * SWR mutate function bound to the request made in the <InitializeAILinter /> component.
   */
  mutate?: ReturnType<typeof useReadmeApi<AIConfigsRepresentationType>>['mutate'];
};

interface AiLinterSliceAction {
  initialize: (
    options: Partial<AiLinterSliceState> &
      Required<Pick<AiLinterSliceState, 'mutate'>> & {
        apiBaseUrl: string;
      },
  ) => void;
  /**
   * Updates all prompts at once. Sends a PUT request to the API and modifies
   * the loading state while the request is in progress.
   */
  updateAllPrompts: (settings: Partial<AiLinterSliceState['settings']>) => void;
  /*
   * Updates the prompt for a specific settings ('errors', 'warnings',
   * 'styleguide'). Will send a PUT request to the API and modify the loading
   * state while the request is in progress.
   */
  updatePrompt: (name: keyof AiLinterSliceState['settings'], value: string | null) => void;
}

export interface AiLinterSlice {
  linter: AiLinterSliceAction & AiLinterSliceState;
}

const initialState: AiLinterSliceState = {
  isReady: false,
  enabled: true,
  settings: {
    errors: null,
    warnings: null,
    styleguide: null,
  },
  isSaving: {
    errors: true,
    warnings: true,
    styleguide: true,
  },
};

export const createAiLinterSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiLinterSlice
> = (set, get) => ({
  linter: {
    ...initialState,

    initialize: options => {
      const { apiBaseUrl, enabled, settings, mutate } = options;
      set(
        state => {
          state.linter.apiUrl = `${apiBaseUrl}/ai/configs`;
          state.linter.enabled = enabled ?? false;
          if (settings) {
            state.linter.settings = settings;
          }
          state.linter.isSaving = {
            errors: false,
            warnings: false,
            styleguide: false,
          };
          state.linter.isReady = true;
          /**
           * @todo replace this with an SWR key b/c our store should only hold
           * serializable data and `mutate` is a function`.
           */
          state.linter.mutate = mutate;
        },
        false,
        actionLog('ai.linter.initialize', options),
      );
    },

    updateAllPrompts: async settings => {
      const apiUrl = get().linter.apiUrl;
      const mutate = get().linter.mutate;
      if (
        !apiUrl ||
        get().linter.isSaving.errors ||
        get().linter.isSaving.warnings ||
        get().linter.isSaving.styleguide ||
        !mutate
      ) {
        return;
      }

      set(
        state => {
          state.linter.isSaving = {
            errors: true,
            warnings: true,
            styleguide: true,
          };
        },
        false,
        actionLog('ai.linter.updateAllPrompts', [settings, 'isSaving']),
      );

      try {
        const request = fetcher<UpdateAIConfigsRepresentationType>(apiUrl, {
          body: JSON.stringify({ data: { settings } }),
          method: 'PUT',
        });

        const getNewCacheValue = request.then(({ data }) => ({
          data: produce(get().linter, draft => {
            draft.settings = {
              ...draft.settings,
              ...data.settings,
            };
          }),
        }));

        await mutate(getNewCacheValue, { revalidate: false });
        await request;

        set(
          state => {
            state.linter.settings = {
              ...state.linter.settings,
              ...settings,
            };
            state.linter.isSaving = {
              errors: false,
              warnings: false,
              styleguide: false,
            };
          },
          false,
          actionLog('ai.linter.updateAllPrompts', [settings]),
        );
      } catch (error) {
        // @todo: notification
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },

    updatePrompt: async (name, value) => {
      const apiUrl = get().linter.apiUrl;
      const mutate = get().linter.mutate;
      if (!apiUrl || get().linter.isSaving[name] || !mutate) {
        return;
      }

      set(
        state => {
          state.linter.isSaving[name] = true;
        },
        false,
        actionLog('ai.linter.updatePrompt', [name, 'isSaving']),
      );

      try {
        const request = fetcher<UpdateAIConfigsRepresentationType>(apiUrl, {
          body: JSON.stringify({ data: { settings: { [name]: value } } }),
          method: 'PUT',
        });

        const getNewCacheValue = request.then(({ data }) => ({
          data: produce(get().linter, draft => {
            draft.settings[name] = data.settings[name] || null;
          }),
        }));

        await mutate(getNewCacheValue, { revalidate: false });
        await request;

        set(
          state => {
            state.linter.settings[name] = value;
            state.linter.isSaving[name] = false;
          },
          false,
          actionLog('ai.linter.updatePrompt', [name, value]),
        );
      } catch (error) {
        // @todo: notification
        // eslint-disable-next-line no-console
        console.error(error);
        set(
          state => {
            state.linter.isSaving[name] = false;
          },
          false,
          actionLog('ai.linter.updatePrompt', [name, value]),
        );
      }
    },
  },
});
